import "./CompareProduct.css";
import { ArrowLeft, EyeOff, Eye, X } from "lucide-react";
import ArrowDown from "../../assets/icons/Group 33921.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import ApiService from "../../components/Api_services";
import TV from "../../assets/img/tv-251854.jpg";
import FallbackImg from "../../components/FallbackImg";

const CompareProductv2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideTheSame, setHideTheSame] = useState(false);
  const isLoggedIn = !!localStorage.getItem("access_token");
  // 获取URL参数中的item_id
  const getItemIds = () => {
    const params = new URLSearchParams(location.search);
    return params.get("ids") || params.get("item_id") || "";
  };

  const fetchCompareData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(location.search);
      const itemIds = params.get("item_id") || "";
      const packageIds = params.get("package_id") || "";
      // fallback to legacy 'ids' param if present
      const legacyIds = params.get("ids") || params.get("item_id");

      if (!itemIds && !packageIds && !legacyIds) {
        setError("No products selected for comparison");
        return;
      }

      let result;
      // If we have explicit item_id/package_id, pass object to ApiService (backend should accept)
      if (itemIds || packageIds) {
        result = await ApiService.comparelist({
          item_id: itemIds || "",
          package_id: packageIds || "",
        });
      } else {
        result = await ApiService.comparelist(legacyIds);
      }

      if (result.status === 200) {
        setCompareData(result.data);
      } else {
        setError(result.message || "Failed to fetch comparison data");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(
        "Network error: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompareData();
    localStorage.removeItem("compareIds");
  }, [location.search]);

  const removeProduct = (itemId) => {
    if (compareData && compareData.items) {
      const filteredItems = compareData.items.filter(
        (item) => Number(item.item_id) !== Number(itemId),
      );

      if (filteredItems.length < 2) {
        navigate(-1);
        return;
      }

      // Determine whether remaining set contains bundles => use package_id, otherwise item_id
      const packageIds = [];
      const itemIds = [];
      filteredItems.forEach((it) => {
        const isBundle = String(it.is_bundle) === "1" || Boolean(it.package_id);
        if (isBundle && it.package_id) {
          packageIds.push(String(it.package_id));
        } else {
          itemIds.push(String(it.item_id));
        }
      });

      if (packageIds.length > 0 && itemIds.length === 0) {
        // only packages remain
        navigate(`/compareproduct?package_id=${packageIds.join(",")}`, {
          replace: true,
        });
      } else if (packageIds.length === 0 && itemIds.length > 0) {
        // only items remain
        navigate(`/compareproduct?item_id=${itemIds.join(",")}`, {
          replace: true,
        });
      } else {
        // Mixed (prefer package-only requests): if any package exists, send package_ids only
        if (packageIds.length > 0) {
          navigate(`/compareproduct?package_id=${packageIds.join(",")}`, {
            replace: true,
          });
        } else {
          navigate(`/compareproduct?item_id=${itemIds.join(",")}`, {
            replace: true,
          });
        }
      }
    }
  };

  // 获取所有规格的唯一键
  const getAllSpecificationKeys = () => {
    if (!compareData || !compareData.items) return [];

    const allKeys = new Set();
    compareData.items.forEach((item) => {
      if (item.specification) {
        item.specification.forEach((spec) => {
          allKeys.add(spec.spec_type || spec.name);
        });
      }
    });
    return Array.from(allKeys);
  };

  // 获取特定产品的规格值
  const getSpecificationValue = (item, specType) => {
    if (!item.specification) return "-";
    const spec = item.specification.find(
      (s) => s.spec_type === specType || s.name === specType,
    );
    return spec ? spec.spec_attr || spec.value : "-";
  };

  // 获取特定产品的功能值
  const getFeatureValue = (item, featType) => {
    if (!item.features) return "-";
    const feature = item.features.find((f) => f.feat_type === featType);
    return feature ? feature.feat_attr : "-";
  };

  // 获取所有功能的唯一键
  const getAllFeatureKeys = () => {
    if (!compareData || !compareData.items) return [];

    const allKeys = new Set();
    compareData.items.forEach((item) => {
      if (item.features) {
        item.features.forEach((feature) => {
          allKeys.add(feature.feat_type);
        });
      }
    });
    return Array.from(allKeys);
  };

  const getAllPackingKeys = () => {
    if (!compareData || !compareData.items) return [];

    // 收集所有 packing 类型（uom_name），包括 PCS 1、PCS 2...
    let pcsTypes = [];
    const ctnTypes = [];
    const boxTypes = [];
    const otherTypes = [];

    compareData.items.forEach((item) => {
      if (item.packing && item.packing.length > 0) {
        item.packing.forEach((pack) => {
          const packType = pack.uom_name || "UNIT";
          if (/^PCS(\s*\d*)?$/.test(packType)) {
            if (!pcsTypes.includes(packType)) pcsTypes.push(packType);
          } else if (/^CTN(\s*\d*)?$/.test(packType)) {
            if (!ctnTypes.includes(packType)) ctnTypes.push(packType);
          } else if (/^BOX(\s*\d*)?$/.test(packType)) {
            if (!boxTypes.includes(packType)) boxTypes.push(packType);
          } else {
            if (!otherTypes.includes(packType)) otherTypes.push(packType);
          }
        });
      }
    });

    // 让 'PCS' 排在所有 'PCS n' 前面
    pcsTypes = pcsTypes.sort((a, b) => {
      if (a === "PCS") return -1;
      if (b === "PCS") return 1;
      // 其它 PCS n 按数字升序
      const numA = parseInt(a.replace("PCS", "").trim()) || 0;
      const numB = parseInt(b.replace("PCS", "").trim()) || 0;
      return numA - numB;
    });

    // 按顺序拼接所有类型
    const sortedTypes = [...pcsTypes, ...ctnTypes, ...boxTypes, ...otherTypes];

    // 为每个包装类型按顺序添加 dimensions, weight, cbm
    const orderedKeys = [];
    sortedTypes.forEach((packType) => {
      orderedKeys.push(`${packType}_dimensions`);
      orderedKeys.push(`${packType}_weight`);
      orderedKeys.push(`${packType}_cbm`);
    });

    return orderedKeys;
  };

  // 获取特定产品的包装值
  const getPackingValue = (item, packingKey) => {
    if (!item.packing || !item.packing.length) return "-";

    const [packType, valueType] = packingKey.split("_");
    const packingData = item.packing.find((pack) => pack.uom_name === packType);

    if (!packingData) return "-";

    switch (valueType) {
      case "dimensions":
        if (
          packingData.packing_dimension_l &&
          packingData.packing_dimension_w &&
          packingData.packing_dimension_h
        ) {
          return `${packingData.packing_dimension_l} × ${packingData.packing_dimension_w} × ${packingData.packing_dimension_h} cm`;
        }
        return "-";

      case "weight":
        return packingData.packing_weight
          ? `${packingData.packing_weight} kg`
          : "-";

      case "cbm":
        if (
          packingData.packing_dimension_l &&
          packingData.packing_dimension_w &&
          packingData.packing_dimension_h
        ) {
          const volume = (
            (parseFloat(packingData.packing_dimension_l) *
              parseFloat(packingData.packing_dimension_w) *
              parseFloat(packingData.packing_dimension_h)) /
            1000000
          ).toFixed(4);
          return `${volume} m³`;
        }
        return "-";

      default:
        return "-";
    }
  };

  // 格式化包装键名显示
  const formatPackingKeyName = (key) => {
    const [packType, valueType] = key.split("_");

    switch (valueType) {
      case "dimensions":
        return `Packaging Dimensions (${packType} - L x W x H)`;
      case "weight":
        return `Packaging Weight (${packType})`;
      case "cbm":
        return `Packaging CBM (${packType})`;
      default:
        return key;
    }
  };

  // 检查是否应该隐藏相同的行
  const shouldHideRow = (values) => {
    if (!hideTheSame) return false;
    return values.every((value) => value === values[0]);
  };

  const getProductPricing = (item, isLoggedIn) => {
    if (!isLoggedIn) {
      // 未登录一律用 rcp
      const rcp = parseFloat(item.product?.[0]?.rcp || 0);
      return {
        displayPrice: rcp,
        originalPrice: null,
        discountPercent: 0,
      };
    }

    const lowestPrice = parseFloat(item.pricing?.lowest_price || 0);
    const brandPrice = parseFloat(item.pricing?.brand_price || 0);

    if (lowestPrice > 0) {
      let discountPercent = 0;
      if (brandPrice > 0 && lowestPrice < brandPrice) {
        discountPercent = Math.round(
          ((brandPrice - lowestPrice) / brandPrice) * 100,
        );
      }
      return {
        displayPrice: lowestPrice,
        originalPrice:
          brandPrice > 0 && lowestPrice < brandPrice ? brandPrice : null,
        discountPercent,
      };
    }
    return {
      displayPrice: brandPrice,
      originalPrice: null,
      discountPercent: 0,
    };
  };

  if (loading) {
    return (
      <div className="compareproduct_v2">
        <div className="compareproduct-content_v2">
          <div className="loading_v2">Loading comparison data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="compareproduct_v2">
        <div className="compareproduct-content_v2">
          <div className="error_v2">Error: {error}</div>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!compareData || !compareData.items || compareData.items.length === 0) {
    return (
      <div className="compareproduct_v2">
        <div className="compareproduct-content_v2">
          <div className="no-data_v2">No products to compare</div>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const specificationKeys = getAllSpecificationKeys();
  const featureKeys = getAllFeatureKeys();
  const packingKeys = getAllPackingKeys();

  return (
    <div className="compareproduct_v2">
      <div className="compareproduct-content_v2">
        <div className="compareproduct-main-container_v2">
          <div className="cpback-button-margin_v2">
            <div className="cpback-button-container_v2">
              <button className="cpback-button_v2" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
                Back to Products
              </button>

              <button
                type="button"
                className={`compareproduct-toggle-btn_v2 ${hideTheSame ? "active_v2" : ""}`}
                onClick={() => setHideTheSame((prev) => !prev)}
                aria-pressed={hideTheSame}
              >
                {hideTheSame ? (
                  <div>
                    <Eye size={16} /> Show All
                  </div>
                ) : (
                  <div>
                    <EyeOff size={16} /> Hide Same Values
                  </div>
                )}
              </button>
            </div>
          </div>

          <div className="compareproduct-main-margin_v2">
            <div className="compareproduct-main_v2">
              <div style={{ width: "25%" }}>Product</div>

              {compareData.items.map((item) => {
                const isBundle =
                  String(item.is_bundle) === "1" || Boolean(item.package_id);
                const displayPhoto =
                  (isBundle &&
                    (item.bundle_photo || item.bundle_detail?.photo_url)) ||
                  item.product?.[0]?.photo_url ||
                  "/default-product.jpg";
                const displayCode = isBundle
                  ? item.bundle_code ||
                    item.bundle_detail?.package_name ||
                    item.item_id
                  : item.product?.[0]?.item_code || item.item_id;
                const displayName = isBundle
                  ? item.bundle_name ||
                    item.bundle_detail?.package_name ||
                    item.product?.[0]?.item_name
                  : item.product?.[0]?.item_name || "Product Name";
                const packageId = isBundle
                  ? (item.package_id ?? item.bundle_detail?.package_id ?? null)
                  : null;
                // ProductDetail reads isBundle/bundleId from the URL (same as ProductList),
                // so the region (branch state) popup loads the right item/bundle.
                const detailParams = new URLSearchParams();
                if (isBundle) {
                  detailParams.set("isBundle", "1");
                  if (packageId) detailParams.set("bundleId", String(packageId));
                }
                const detailPath = `/productdetail/${item.item_id}${detailParams.toString() ? `?${detailParams.toString()}` : ""}`;
                const detailState = { isBundle, packageId, from: "compareproduct" };

                return (
                  <div key={item.item_id} className="compareproduct-maincol_v2">
                    <div className="cp-image-wrapper_v2">
                      <button
                        className="cp-remove-btn_v2"
                        onClick={() => removeProduct(item.item_id)}
                      >
                        <X size={16} />
                      </button>
                      <Link
                        className="cp-image-link_v2"
                        to={detailPath}
                        state={detailState}
                      >
                        <FallbackImg
                          src={displayPhoto}
                          alt={displayName}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                      </Link>
                    </div>
                    <div className="compareproduct-code_v2">
                      {displayCode || "--"}
                    </div>
                    <div className="compareproduct-name_v2" title={displayName}>
                      {displayName || "--"}
                    </div>
                    {/* Pricing Info */}
                    <div className="compareproduct-price-info_v2">
                      {(() => {
                        const { displayPrice, originalPrice, discountPercent } =
                          getProductPricing(item, isLoggedIn);
                        if (displayPrice > 0) {
                          return (
                            <>
                              <div className="price_v2 compare-product">
                                RM {displayPrice.toFixed(2)}
                              </div>
                              {originalPrice && (
                                <div className="prediscount_v2">
                                  RM {originalPrice.toFixed(2)}
                                </div>
                              )}
                            </>
                          );
                        }
                        return <span>--</span>;
                      })()}
                    </div>
                    <button
                      className="compareproduct-linkto-btn"
                      // Same page as the image link above: this item's product detail.
                      onClick={() =>
                        navigate(detailPath, { state: detailState })
                      }
                    >
                      View Details
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="compareproduct-list_v2">
          {/* 基本信息行 */}
          <div className="compareproduct-row_v2">
            <div className="compareproduct-row-inner_v2">
              <div className="compareproduct-subtitle_v2">Brand</div>
              {compareData.items.map((item) => (
                <div key={item.item_id} className="compareproduct-col_v2">
                  {item.product?.[0]?.brand_name || "-"}
                </div>
              ))}
            </div>
          </div>

          {/* 动态规格行 */}
          {specificationKeys.map((specType) => {
            const values = compareData.items.map((item) =>
              getSpecificationValue(item, specType),
            );

            if (shouldHideRow(values)) {
              return null;
            }

            return (
              <div key={specType} className="compareproduct-row_v2">
                <div className="compareproduct-row-inner_v2">
                  <div className="compareproduct-subtitle_v2">{specType}</div>
                  {values.map((value, index) => (
                    <div key={index} className="compareproduct-col_v2">
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* 动态功能行 */}
          {featureKeys.map((featType) => {
            const values = compareData.items.map((item) =>
              getFeatureValue(item, featType),
            );

            if (shouldHideRow(values)) {
              return null;
            }

            return (
              <div key={featType} className="compareproduct-row_v2">
                <div className="compareproduct-row-inner_v2">
                  <div className="compareproduct-subtitle_v2">{featType}</div>
                  {values.map((value, index) => (
                    <div key={index} className="compareproduct-col_v2">
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* 包装信息行 */}
          {packingKeys.map((packingKey) => {
            const values = compareData.items.map((item) =>
              getPackingValue(item, packingKey),
            );

            if (hideTheSame && shouldHideRow(values)) {
              return null;
            }

            return (
              <div key={packingKey} className="compareproduct-row_v2">
                <div className="compareproduct-row-inner_v2">
                  <div className="compareproduct-subtitle_v2">
                    {formatPackingKeyName(packingKey)}
                  </div>
                  {compareData.items.map((item, index) => (
                    <div key={index} className="compareproduct-col_v2">
                      {getPackingValue(item, packingKey)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CompareProductv2;
